import { updateAccountableBankingSettings } from 'api/v2/users/settings';
import { Button } from 'components/atoms/Button';
import { Modal } from 'components/atoms/Modal';
import ModalHeading from 'components/molecules/ModalHeading';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { UserCacheKeys } from 'types/cacheKeys.types';
import {
  isAlertDismissed,
  markAlertAsDismissed,
} from 'utils/AlertsStoreManager';
import { logAmplitude } from 'utils/amplitude';
import { WEBAPP_BANKING_ONBOARDING_DISMISSEDSETASIDEBANNER } from 'utils/amplitude/events/bank.amplitude';
import showToast from 'utils/toast';
import BankIcon from 'assets/svg/icons/bankIcon';

const LOCAL_DISMISS_KEY = 'ab_entry_points_dismissed';

const useAccountableBankingEntryPoints = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: user } = useGetCurrentCustomerData();

  const [isLocallyDismissed, setIsLocallyDismissed] = useState(() =>
    isAlertDismissed(LOCAL_DISMISS_KEY),
  );
  const [isDismissDialogOpen, setIsDismissDialogOpen] = useState(false);
  const dismissSourceRef = useRef<string>();

  const shouldHideEntryPoints =
    user?.settings?.accountableBanking?.entryPointsHidden === true ||
    isLocallyDismissed;

  const trackDismiss = (type: 'local' | 'permanent') => {
    if (dismissSourceRef.current) {
      logAmplitude(WEBAPP_BANKING_ONBOARDING_DISMISSEDSETASIDEBANNER, {
        source: dismissSourceRef.current,
        type,
      });
    }
  };

  const { mutate, isLoading } = useMutation(
    () => updateAccountableBankingSettings({ entryPointsHidden: true }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(UserCacheKeys.UserData);
        trackDismiss('permanent');
        showToast(
          {
            variant: 'info',
            title: t('banking.join_banking_cta.dismiss.toast.title'),
          },
          { autoClose: 10000 },
        );
      },
    },
  );

  const hideEntryPoints = (
    source: 'expense_form' | 'main_bank_page' | 'cash_available',
  ) => {
    dismissSourceRef.current = source;
    setIsDismissDialogOpen(true);
  };

  const handleLocalDismiss = () => {
    markAlertAsDismissed(LOCAL_DISMISS_KEY);
    setIsLocallyDismissed(true);
    trackDismiss('local');
    setIsDismissDialogOpen(false);
  };

  const handlePermanentDismiss = () => {
    mutate();
    setIsDismissDialogOpen(false);
  };

  const dismissDialog = isDismissDialogOpen ? (
    <Modal
      color="bank"
      name="Dismiss Accountable Banking entry points"
      defaultOpen
      onClose={() => setIsDismissDialogOpen(false)}
      width="lg"
      render={() => (
        <>
          <ModalHeading
            Icon={BankIcon}
            title={t('banking.join_banking_cta.dismiss.title')}
            color="bank"
            subtitle={t('banking.join_banking_cta.dismiss.subtitle')}
          />
          <div className="flex space-x-3">
            <Button
              color="bank"
              onClick={handlePermanentDismiss}
              loading={isLoading}
              className="flex-1"
              structure="secondary"
            >
              {t('banking.join_banking_cta.dismiss.button.dismiss_permanently')}
            </Button>
            <Button
              color="bank"
              onClick={handleLocalDismiss}
              className="flex-1"
            >
              {t(
                'banking.join_banking_cta.dismiss.button.hide_only_on_this_browser',
              )}
            </Button>
          </div>
        </>
      )}
    />
  ) : null;

  return {
    shouldHideEntryPoints,
    hideEntryPoints,
    dismissDialog,
    isLoading,
  };
};

export default useAccountableBankingEntryPoints;
